import { matchPath } from "react-router-dom";
import { HttpError } from "@/api/client";
import { getRuntimeConfig, trackingEnabled } from "@/config/runtime";

export interface ClientEvent {
  eventId: string;
  sessionId: string;
  event: string;
  properties: Record<string, string | number | boolean | null>;
  routeTemplate: string;
  timestamp: string;
  sessionStartedAt: string;
}

export interface EventBatch {
  events: ClientEvent[];
}

export interface RouteContext {
  routeTemplate: string;
  pageType: "home" | "district" | "school" | "class" | "student" | "print" | "login" | "theme-smoke";
  params: {
    districtId?: string;
    schoolId?: string;
    classId?: string;
    studentId?: string;
  };
}

interface PersistedRetryBatch {
  attempts: number;
  events: ClientEvent[];
}

interface TrackerConfig {
  enabled: boolean;
  postEvents: (events: ClientEvent[], init?: RequestInit) => Promise<void>;
}

interface TrackInput {
  event: string;
  properties: Record<string, string | number | boolean | null>;
  routeContext: RouteContext;
}

interface RoutePattern {
  pageType: RouteContext["pageType"];
  pattern: string;
}

const SESSION_ID_KEY = "tracker-session-id-v1";
const SESSION_STARTED_AT_KEY = "tracker-session-started-at-v1";
const RETRY_BATCH_KEY = "tracker-retry-batch-v1";
const FLUSH_INTERVAL_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 60_000;
const REQUEST_TIMEOUT_MS = 5_000;
const MAX_BEACON_BYTES = 48 * 1024;

const ROUTE_PATTERNS: readonly RoutePattern[] = [
  {
    pageType: "print",
    pattern: "/districts/:districtId/schools/:schoolId/classes/:classId/students/:studentId/print",
  },
  {
    pageType: "student",
    pattern: "/districts/:districtId/schools/:schoolId/classes/:classId/students/:studentId",
  },
  {
    pageType: "class",
    pattern: "/districts/:districtId/schools/:schoolId/classes/:classId",
  },
  {
    pageType: "school",
    pattern: "/districts/:districtId/schools/:schoolId",
  },
  {
    pageType: "district",
    pattern: "/districts/:districtId",
  },
  {
    pageType: "login",
    pattern: "/login",
  },
  {
    pageType: "theme-smoke",
    pattern: "/theme-smoke",
  },
  {
    pageType: "home",
    pattern: "/",
  },
] as const;

function generateUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `fallback-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function isSubstantivePage(routeContext: RouteContext | null): boolean {
  if (!routeContext) return false;
  return !["login", "theme-smoke", "print"].includes(routeContext.pageType);
}

function isHighPriorityEvent(eventName: string): boolean {
  return [
    "session_end",
    "student_viewed",
    "student_opened",
    "print_initiated",
    "export_csv_clicked",
  ].includes(eventName);
}

function serializeEventBatch(events: ClientEvent[]): string {
  return JSON.stringify({ events });
}

function serializedBatchSize(events: ClientEvent[]): number {
  if (typeof Blob !== "undefined") {
    return new Blob([serializeEventBatch(events)], { type: "application/json" }).size;
  }

  return serializeEventBatch(events).length;
}

function sortEventsForPruning(events: ClientEvent[]): ClientEvent[] {
  const heartbeats = events.filter((event) => event.event === "session_heartbeat");
  const pageViews = events.filter((event) => event.event === "page_view");
  const protectedEvents = events.filter(
    (event) => event.event !== "session_heartbeat" && event.event !== "page_view",
  );
  return [...heartbeats, ...pageViews, ...protectedEvents];
}

function pruneEventsForBeacon(events: ClientEvent[]): ClientEvent[] {
  const pruned = [...events];
  if (serializedBatchSize(pruned) <= MAX_BEACON_BYTES) return pruned;

  const removable = sortEventsForPruning(pruned).filter((event) => !isHighPriorityEvent(event.event));
  for (const candidate of removable) {
    const index = pruned.findIndex((event) => event.eventId === candidate.eventId);
    if (index >= 0) pruned.splice(index, 1);
    if (serializedBatchSize(pruned) <= MAX_BEACON_BYTES) break;
  }

  return pruned;
}

export function getRouteContext(pathname: string): RouteContext | null {
  for (const { pattern, pageType } of ROUTE_PATTERNS) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (!match) continue;
    return {
      routeTemplate: pattern,
      pageType,
      params: {
        districtId: match.params.districtId,
        schoolId: match.params.schoolId,
        classId: match.params.classId,
        studentId: match.params.studentId,
      },
    };
  }

  return null;
}

class EngagementTracker {
  private enabled = false;
  private postEvents: ((events: ClientEvent[], init?: RequestInit) => Promise<void>) | null = null;
  private configured = false;
  private listenersBound = false;
  private flushing = false;
  private heartbeatTimer: number | null = null;
  private flushTimer: number | null = null;
  private liveQueue: ClientEvent[] = [];
  private currentPathname: string | null = null;
  private currentRouteContext: RouteContext | null = null;
  private currentPageEnteredAt: number | null = null;
  private sessionStarted = false;
  private pageViewsCount = 0;
  private studentsViewed = new Set<string>();
  private unloadHandled = false;

  configure(config: TrackerConfig): void {
    this.enabled = config.enabled;
    this.postEvents = config.postEvents;
    this.configured = true;

    if (!this.enabled) return;
    this.bindTimersAndListeners();
  }

  onRouteChange(pathname: string, routeContext: RouteContext | null, suppressPageView = false): void {
    if (!this.enabled || !routeContext) return;

    this.ensureSession(routeContext.routeTemplate);
    const pathnameChanged = this.currentPathname !== pathname;

    if (pathnameChanged) {
      this.enqueueCurrentPageDuration();
      this.currentPathname = pathname;
      this.currentRouteContext = routeContext;
      this.currentPageEnteredAt = Date.now();
    }

    if (!pathnameChanged || suppressPageView || !isSubstantivePage(routeContext)) return;

    this.enqueue("page_view", this.routeProperties(routeContext), routeContext);
    this.pageViewsCount += 1;
  }

  track({ event, properties, routeContext }: TrackInput): void {
    if (!this.enabled) return;

    this.ensureSession(routeContext.routeTemplate);
    this.enqueue(event, properties, routeContext);

    if (event === "student_viewed" && typeof properties.student_id === "string") {
      this.studentsViewed.add(properties.student_id);
    }
  }

  flushSoon(): void {
    if (!this.enabled) return;
    void this.flush();
  }

  resetForTests(): void {
    this.enabled = false;
    this.postEvents = null;
    this.configured = false;
    this.listenersBound = false;
    this.liveQueue = [];
    this.currentPathname = null;
    this.currentRouteContext = null;
    this.currentPageEnteredAt = null;
    this.sessionStarted = false;
    this.pageViewsCount = 0;
    this.studentsViewed.clear();
    this.unloadHandled = false;

    if (this.heartbeatTimer != null && typeof window !== "undefined") {
      window.clearInterval(this.heartbeatTimer);
    }
    if (this.flushTimer != null && typeof window !== "undefined") {
      window.clearInterval(this.flushTimer);
    }

    this.heartbeatTimer = null;
    this.flushTimer = null;

    if (typeof window !== "undefined") {
      window.removeEventListener("beforeunload", this.handleUnload);
      window.removeEventListener("pagehide", this.handleUnload);
    }

    const storage = getSessionStorage();
    storage?.removeItem(SESSION_ID_KEY);
    storage?.removeItem(SESSION_STARTED_AT_KEY);
    storage?.removeItem(RETRY_BATCH_KEY);
  }

  private bindTimersAndListeners(): void {
    if (this.listenersBound || typeof window === "undefined") return;
    this.listenersBound = true;

    this.flushTimer = window.setInterval(() => {
      void this.flush();
    }, FLUSH_INTERVAL_MS);

    this.heartbeatTimer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const routeContext = this.currentPathname ? getRouteContext(this.currentPathname) : null;
      if (!routeContext) return;
      this.track({
        event: "session_heartbeat",
        properties: { duration_sec: this.sessionDurationSeconds() },
        routeContext,
      });
    }, HEARTBEAT_INTERVAL_MS);

    window.addEventListener("beforeunload", this.handleUnload);
    window.addEventListener("pagehide", this.handleUnload);
  }

  private readonly handleUnload = (): void => {
    if (!this.enabled || this.unloadHandled) return;
    this.unloadHandled = true;

    const routeContext = this.currentPathname ? getRouteContext(this.currentPathname) : null;
    if (routeContext) {
      this.ensureSession(routeContext.routeTemplate);
      this.enqueueCurrentPageDuration();
      this.enqueue("session_end", {
        duration_sec: this.sessionDurationSeconds(),
        pages_viewed: this.pageViewsCount,
        students_viewed: this.studentsViewed.size,
      }, routeContext);
    }

    const beaconPayload = this.buildBeaconPayload();
    if (!beaconPayload.length) return;

    const beaconBody = new Blob([serializeEventBatch(beaconPayload)], { type: "application/json" });
    navigator.sendBeacon(`${getRuntimeConfig().apiBaseUrl}/events`, beaconBody);
  };

  private buildBeaconPayload(): ClientEvent[] {
    const retryBatch = this.getRetryBatch()?.events ?? [];
    return pruneEventsForBeacon([...retryBatch, ...this.liveQueue]);
  }

  private async flush(): Promise<void> {
    if (!this.enabled || !this.configured || !this.postEvents || this.flushing) return;
    this.flushing = true;
    try {
      const retryBatch = this.getRetryBatch();
      if (retryBatch) {
        const replayResult = await this.sendWithRetrySemantics(retryBatch.events, retryBatch.attempts + 1);
        if (!replayResult) return;
      }

      if (!this.liveQueue.length) return;

      const liveEvents = [...this.liveQueue];
      await this.sendWithRetrySemantics(liveEvents, 0);
      // Drain sent events from the live queue regardless of outcome.
      // On success: events delivered. On retryable failure: persistRetryBatch
      // already drained them. On non-retryable failure: drop permanently.
      this.liveQueue = this.liveQueue.filter(
        (event) => !liveEvents.some((candidate) => candidate.eventId === event.eventId),
      );
    } finally {
      this.flushing = false;
    }
  }

  private async sendWithRetrySemantics(events: ClientEvent[], attempts: number): Promise<boolean> {
    if (!events.length || !this.postEvents) return true;

    try {
      await this.postWithTimeout(events);
      this.clearRetryBatch();
      return true;
    } catch (error: unknown) {
      if (!this.shouldRetry(error)) {
        this.clearRetryBatch();
        return false;
      }

      if (attempts >= 1) {
        this.clearRetryBatch();
        this.warnDrop(error);
        return false;
      }

      this.persistRetryBatch({ attempts, events });
      return false;
    }
  }

  private async postWithTimeout(events: ClientEvent[]): Promise<void> {
    if (!this.postEvents) return;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort("timeout"), REQUEST_TIMEOUT_MS);
    try {
      await this.postEvents(events.map((event) => ({ ...event })), { signal: controller.signal });
    } catch (error: unknown) {
      if (
        (error instanceof DOMException && error.name === "AbortError")
        || (error instanceof Error && controller.signal.aborted)
      ) {
        throw new Error("Tracking request timed out", { cause: error });
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  private shouldRetry(error: unknown): boolean {
    if (error instanceof HttpError) {
      return isRetryableStatus(error.status);
    }
    if (error instanceof Error && error.message === "Tracking request timed out") return true;
    return true;
  }

  private warnDrop(error: unknown): void {
    if (!trackingEnabled() || !import.meta.env.DEV) return;
    console.warn("Dropping analytics retry batch after repeated failure", error);
  }

  private ensureSession(entryRouteTemplate: string): void {
    if (this.sessionStarted) return;
    this.sessionStarted = true;
    this.enqueue("session_start", { entry_route_template: entryRouteTemplate }, {
      routeTemplate: entryRouteTemplate,
      pageType: "home",
      params: {},
    });
  }

  private enqueueCurrentPageDuration(): void {
    const routeContext = this.currentRouteContext;
    if (!routeContext || !isSubstantivePage(routeContext) || this.currentPageEnteredAt == null) return;

    const durationSec = Math.max(0, Math.round((Date.now() - this.currentPageEnteredAt) / 1000));
    if (durationSec <= 0) return;

    this.enqueue("page_duration", {
      ...this.routeProperties(routeContext),
      duration_sec: durationSec,
    }, routeContext);
  }

  private routeProperties(routeContext: RouteContext): Record<string, string | number | boolean | null> {
    return {
      page_type: routeContext.pageType,
      district_id: routeContext.params.districtId ?? null,
      school_id: routeContext.params.schoolId ?? null,
      class_id: routeContext.params.classId ?? null,
      student_id: routeContext.params.studentId ?? null,
    };
  }

  private enqueue(
    event: string,
    properties: Record<string, string | number | boolean | null>,
    routeContext: RouteContext,
  ): void {
    const nextEvent: ClientEvent = {
      eventId: generateUuid(),
      sessionId: this.getOrCreateSessionId(),
      event,
      properties,
      routeTemplate: routeContext.routeTemplate,
      timestamp: new Date().toISOString(),
      sessionStartedAt: this.getOrCreateSessionStartedAt(),
    };

    this.liveQueue.push(nextEvent);

    if (this.liveQueue.length >= 10) {
      void this.flush();
    }
  }

  private getOrCreateSessionId(): string {
    const storage = getSessionStorage();
    const existing = storage?.getItem(SESSION_ID_KEY);
    if (existing) return existing;

    const next = generateUuid();
    storage?.setItem(SESSION_ID_KEY, next);
    return next;
  }

  private getOrCreateSessionStartedAt(): string {
    const storage = getSessionStorage();
    const existing = storage?.getItem(SESSION_STARTED_AT_KEY);
    if (existing) return existing;

    const next = new Date().toISOString();
    storage?.setItem(SESSION_STARTED_AT_KEY, next);
    return next;
  }

  private sessionDurationSeconds(): number {
    const startedAt = this.getOrCreateSessionStartedAt();
    return Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 1000));
  }

  private getRetryBatch(): PersistedRetryBatch | null {
    const storage = getSessionStorage();
    const raw = storage?.getItem(RETRY_BATCH_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as PersistedRetryBatch;
    } catch {
      storage?.removeItem(RETRY_BATCH_KEY);
      return null;
    }
  }

  private persistRetryBatch(batch: PersistedRetryBatch): void {
    const storage = getSessionStorage();
    storage?.setItem(RETRY_BATCH_KEY, JSON.stringify(batch));
    this.liveQueue = this.liveQueue.filter(
      (event) => !batch.events.some((candidate) => candidate.eventId === event.eventId),
    );
  }

  private clearRetryBatch(): void {
    const storage = getSessionStorage();
    storage?.removeItem(RETRY_BATCH_KEY);
  }
}

const trackerSingleton = new EngagementTracker();

export function getEngagementTracker(): EngagementTracker {
  return trackerSingleton;
}

export function resetTrackerForTests(): void {
  trackerSingleton.resetForTests();
}
